import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import TextInput from 'src/components/UI/input/TextInput';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { stripMemberReferencesToPid } from '../../pedigree/edgeIntegrity';
import {
  deleteConsequences,
  recordsNote,
  RECORDS_NOTE_UNKNOWN,
  ROOT_RECORDS_NOTE,
  CHILDREN_KEPT_NOTE
} from '../../pedigree/deleteConsequences';
import { usePruneOrderForStructuralEdit } from '../../structuralEditContext';

function DeleteNode({
  node,
  user,
  memberRecords,
  setMemberRecords,
  handleClose
}) {
  const [inputValue, setInputValue] = React.useState('');
  const [error, setError] = React.useState(false);
  const { setSnack } = React.useContext(SnackContext);
  const { dispatchUpdate } = React.useContext(AppContext);
  const pruneOrderForStructuralEdit = usePruneOrderForStructuralEdit();
  // The records as of the latest render, for the order prune after the POST —
  // the render-time `memberRecords` in proceedToDelete's closure may be stale by
  // then, for the same reason the member update below derives from `prev`.
  const latestMembersRef = React.useRef(memberRecords);
  latestMembersRef.current = memberRecords;

  const onChange = (e) => {
    setInputValue(e.target.value);
    setError(false);
  };

  const isStructuralProband = !node.familyMemberId && !!node.proBandId;

  // What the deletion does to the people around them. The CHILDREN do not go —
  // they survive and are orphaned — which is exactly what this dialog never said
  // before TGCLOUD-1644. (The person's own clinical records DO go, since
  // TGCLOUD-1848; that line is fetched below.) Lines that would read "0" are
  // already filtered out, so a leaf node shows no bullets at all rather than a
  // list of zeroes.
  const consequences = React.useMemo(
    () => deleteConsequences(node, memberRecords),
    [node, memberRecords]
  );

  // The person's clinical records are the one consequence with no client-side
  // source, so the dialog asks — and the answer is a DRY RUN of the delete itself
  // (TGCLOUD-1848), so it counts exactly what will go. 'pending' shows nothing at
  // all — a line that appears a beat after the dialog opens would be worse than one
  // that is simply absent — and 'unknown' (the request failed) falls back to
  // unconditional wording, because failing to warn about a real deletion is worse
  // than a moment's pause. The family's original proband is deleted by re-rooting
  // the family, which has no dry run, so that line is stated plainly.
  const [impact, setImpact] = React.useState('pending');
  const impactRef = node.familyMemberId;
  React.useEffect(() => {
    if (isStructuralProband || !impactRef || !user?._id) {
      setImpact(null);
      return;
    }
    let live = true;
    axios
      .get(
        APICONSTANTS.pedigreeDeleteImpact
          .replace(':user_id', user._id)
          .replace(':member_id', impactRef)
      )
      .then(({ data }) => live && setImpact(data))
      .catch(() => live && setImpact('unknown'));
    return () => {
      live = false;
    };
  }, [impactRef, user?._id, isStructuralProband]);

  const recordsLine = isStructuralProband
    ? ROOT_RECORDS_NOTE
    : impact === 'pending'
    ? null
    : impact === 'unknown'
    ? RECORDS_NOTE_UNKNOWN
    : recordsNote(impact);

  // Nothing to warn about — a leaf with no records gets no block at all,
  // rather than a reassuring-sounding box that trains people to skip it.
  const hasConsequences = consequences.lines.length > 0 || !!recordsLine;

  const proceedToDelete = async () => {
    const displayedUpn = String(node.upn || node.pid);
    if (String(inputValue).trim() !== displayedUpn) {
      setError(true);
      return;
    }

    const pid = node.pid;

    try {
      if (isStructuralProband) {
        await axios.post(
          APICONSTANTS.deleteStructuralProband.replace(':user_id', user._id),
          { pid }
        );
        const { data } = await axios.get(
          APICONSTANTS.getPedigreeDetails.replace(':familyId', user.familyId)
        );
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: { family: data.families, selectedUser: data.proband }
        });
        handleClose();
        return;
      }

      await axios.post(
        APICONSTANTS.deletePedigreeFamilyMember
          .replace(':user_id', user._id)
          .replace(':member_id', node.familyMemberId)
      );

      // Functional update: state may have moved on while the POST was in
      // flight (save-echo identity backfill, concurrent edits) — derive the
      // deletion from `prev`, never from the render-time snapshot.
      const withoutNode = (list) =>
        list
          .filter((p) => p.pid !== pid)
          .map((p) => stripMemberReferencesToPid(p, pid));
      setMemberRecords((prev) => withoutNode(prev));
      // Removing a person can split the family (their spouse or children may no
      // longer connect to the proband), so the ranks of everyone it moved go in
      // the SAME update (TGCLOUD-1786, structuralEditContext.js).
      const before = latestMembersRef.current || [];
      pruneOrderForStructuralEdit(before, withoutNode(before));
      handleClose();
    } catch {
      setSnack({
        status: true,
        msg: 'Failed to delete family member',
        severity: 'error'
      });
    }
  };

  return (
    <CustomDialog
      open={true}
      handleClose={handleClose}
      showActions={true}
      saveBtnLabel={'Confirm Delete'}
      title={'Delete Confirmation'}
      titleAppearance={'warning'}
      minHeight={200}
      onSave={proceedToDelete}
      allowBackgroundClose={handleClose}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 'bold'
          }}
        >
          {`Warning: This action will permanently delete the following family member's details, including all associated records and data.`}
        </Typography>
        <Box
          sx={{
            py: 2
          }}
        >
          <Typography>UPN : {node.upn || node.pid}</Typography>
          <Typography>
            Full Name: {node.firstName} {node.lastName}
          </Typography>
          <Typography>
            Family Member Id : {node.familyMemberId || node.proBandId}
          </Typography>
          <Typography>Date of Birth: {node.dob}</Typography>
        </Box>
        {hasConsequences && (
          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
              bgcolor: (t) => alpha(t.palette.warning.main, 0.06)
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {consequences.lines.length
                ? 'Removing them will also:'
                : 'Also note:'}
            </Typography>
            {consequences.lines.length > 0 && (
              <Box
                component="ul"
                sx={{ m: 0, mt: 0.5, pl: 2.5, '& li': { mb: 0.25 } }}
              >
                {consequences.lines.map((line) => (
                  <li key={line}>
                    <Typography variant="body2" component="span">
                      {line}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            {recordsLine && (
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                {recordsLine}
              </Typography>
            )}
            {consequences.childCount > 0 && (
              <Typography
                variant="body2"
                sx={{ mt: 0.75, color: 'text.secondary' }}
              >
                {CHILDREN_KEPT_NOTE}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="subtitle2">
          Please note that this action cannot be undone, and all historical
          information linked to this family member will be permanently removed
          from the system.
        </Typography>
        {isStructuralProband && (
          <Typography
            variant="subtitle2"
            sx={{
              color: 'warning.main',
              pt: 1
            }}
          >
            This is the original proband. Deleting them will promote another
            family member to be the new family root. The pedigree will refresh
            after deletion.
          </Typography>
        )}
        <Box
          className="user-form"
          sx={{
            pt: 2
          }}
        >
          <TextInput
            label="Enter Family Member UPN to confirm deletion"
            error={error}
            helperText={error && 'Invalid Family Member UPN'}
            placeholder={String(node.upn || node.pid)}
            onChange={onChange}
          />
        </Box>
      </Box>
    </CustomDialog>
  );
}

export default DeleteNode;
